import { createContext, useContext, useMemo, useEffect, ReactNode } from 'react';
import { useAuthStore } from '@/lib/stores/authStore';
import { getDepartmentTheme, DepartmentTheme } from '@/lib/utils/departmentTheme';

const DepartmentThemeContext = createContext<DepartmentTheme>({
  department: 'default',
  themeClass: '',
  appName: 'Pranik.AI',
  isAyurveda: false,
});

interface DepartmentThemeProviderProps {
  children: ReactNode;
}

export function DepartmentThemeProvider({ children }: DepartmentThemeProviderProps) {
  const user = useAuthStore((state) => state.user);

  const theme = useMemo(() => getDepartmentTheme(user), [user]);

  // Apply theme class to <html> element (same pattern as dark mode)
  useEffect(() => {
    const root = document.documentElement;
    // Remove any existing department theme classes
    root.classList.remove('theme-ayurveda');
    // Add current theme class if any
    if (theme.themeClass) {
      root.classList.add(theme.themeClass);
    }
    return () => {
      root.classList.remove('theme-ayurveda');
    };
  }, [theme.themeClass]);

  return (
    <DepartmentThemeContext.Provider value={theme}>{children}</DepartmentThemeContext.Provider>
  );
}

/**
 * Hook to access the current department theme.
 * Returns department info, theme class, and branding details.
 */
export function useDepartmentTheme(): DepartmentTheme {
  return useContext(DepartmentThemeContext);
}
